'use client';

import {
  useSecureInformationRequestTemplates,
  useServiceRequestsPaginated,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  Input,
  Label,
  Loading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from '@commenda/kakashi';
import { zodResolver } from '@hookform/resolvers/zod';
import { ServiceRequestStatus } from '@prisma/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { getServiceRequestServiceDetails } from '../../service-request/constants';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  linkedService: z.string().optional().nullable(),
  informationRequestTemplate: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateSecureInformationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: {
    id: number;
    name: string;
  };
  agentFirmId?: string;
  linkedServiceRequestId?: string;
  userId: string | null;
  onContinue: (data: FormData, linkedServiceName?: string) => void;
  isAdmin?: boolean;
  isAgentFirmAdmin?: boolean;
}

const CreateSecureInformationRequestModal = ({
  isOpen,
  onClose,
  agentFirmId,
  businessEntity,
  userId,
  onContinue,
  linkedServiceRequestId,
  isAdmin,
  isAgentFirmAdmin,
}: CreateSecureInformationRequestModalProps) => {
  const {
    data: secureInformationRequestTemplatesResponse,
    error: secureInformationRequestTemplatesError,
  } = useSecureInformationRequestTemplates(agentFirmId);

  const { data: serviceRequestsResponse, error: serviceRequestsError } =
    useServiceRequestsPaginated(
      isAgentFirmAdmin && agentFirmId
        ? { assignedAgentFirmId: agentFirmId, corporationId: businessEntity.id }
        : userId
          ? { assignedUserId: userId, corporationId: businessEntity.id }
          : undefined
    );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    reset,
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      linkedService: linkedServiceRequestId || '',
      informationRequestTemplate: '',
    },
  });

  useEffect(() => {
    if (linkedServiceRequestId) {
      setValue('linkedService', linkedServiceRequestId);
    }
  }, [linkedServiceRequestId, setValue]);

  const watchedLinkedService = watch('linkedService');
  const watchedInformationRequestTemplate = watch('informationRequestTemplate');

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: FormData) => {
    if (!isAdmin && !data.linkedService) {
      setError('linkedService', { message: 'Linked service is required' });
      return;
    }

    onContinue(
      data,
      getServiceRequestServiceDetails(
        serviceRequestsResponse?.find(
          (service) => service.id === watchedLinkedService
        )
      ).displayName
    );

    reset();

    onClose();
  };

  if (!secureInformationRequestTemplatesResponse || !serviceRequestsResponse) {
    if (secureInformationRequestTemplatesError)
      console.error(secureInformationRequestTemplatesError);

    if (serviceRequestsError) console.error(serviceRequestsError);

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Information Request</DialogTitle>
          </DialogHeader>
          <div className="p-3xl flex justify-center items-center">
            <Loading />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader className="flex flex-col items-start">
          <DialogTitle>Enter basic details to proceed</DialogTitle>
          <DialogDescription>
            Confirm basic details for this information request for{' '}
            {businessEntity.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 p-3xl">
            <FieldWrapper>
              <div className="flex items-center gap-xs">
                <Label>Name of secure information request</Label>
                <Tooltip content="Give this information request a short, descriptive name for quick recognition.">
                  <Icons.InfoCircle className="h-4 w-4 text-fg-quaternary" />
                </Tooltip>
              </div>
              <Input
                placeholder="Type here"
                {...register('name')}
                error={errors.name?.message}
              />
            </FieldWrapper>

            {/* Linked service */}
            {!isAdmin ? (
              <FieldWrapper>
                <div className="flex items-center gap-xs">
                  <Label>Linked service</Label>
                  <Tooltip content="Select the service request that this information request is linked to.">
                    <Icons.InfoCircle className="h-4 w-4 text-fg-quaternary" />
                  </Tooltip>
                </div>
                <Select
                  value={watchedLinkedService}
                  onValueChange={(value) => {
                    clearErrors('linkedService');
                    setValue('linkedService', value);
                  }}
                  disabled={!!linkedServiceRequestId}
                >
                  <SelectTrigger error={errors.linkedService?.message}>
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceRequestsResponse
                      .filter(
                        (service) =>
                          service.status !== ServiceRequestStatus.COMPLETED &&
                          service.status !== ServiceRequestStatus.ARCHIVED
                      )
                      .map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {getServiceRequestServiceDetails(service).displayName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {errors.linkedService && (
                  <p className="txt-small-regular text-fg-error">
                    {errors.linkedService.message}
                  </p>
                )}
              </FieldWrapper>
            ) : null}

            {/* Information request template (optional) */}
            <FieldWrapper>
              <Label>Information request template (optional)</Label>
              <Select
                value={watchedInformationRequestTemplate}
                onValueChange={(value) =>
                  setValue('informationRequestTemplate', value)
                }
                disabled={
                  secureInformationRequestTemplatesResponse
                    ?.secureInformationRequestTemplates?.length === 0
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      secureInformationRequestTemplatesResponse
                        ?.secureInformationRequestTemplates?.length === 0
                        ? 'No templates available'
                        : 'Select one'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {secureInformationRequestTemplatesResponse?.secureInformationRequestTemplates?.map(
                    (template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.title}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </FieldWrapper>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Confirm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { CreateSecureInformationRequestModal };
